import { UtilService } from 'src/app/core/services/util/util.service';
import { ApplicationRef, Injectable, Injector, OnDestroy, inject, signal } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { map, shareReplay, take, tap, throttleTime } from 'rxjs/operators';
import { BrowserPlatformService } from 'src/app/core/services/browser-platform/browser-platform.service';
import { UserDataService, UserActionType } from './user-data.service';
import { SeriesService } from '../series/series.service';
import type { UserSeriesService } from './user-series.service';
import { YeshivaModels } from 'src/serverTypes/yeshivaTypesDeclare';
import { I18nService } from 'src/app/core/services/i18n/i18n.service';
import { HttpClient } from '@angular/common/http';
import { AppGlobalsModule } from '../app-globals.module';
import { environment } from 'src/environments/environment';
import type { MatDialogRef } from '@angular/material/dialog';
import type { LoginDialogComponent, LoginDialogData } from './login/login-dialog.component';
import { UserStateService } from './user-state.service';

/** how a nihulLogin round trip ended (the cms logon overlay) */
export type NihulLoginOutcome = 'loggedIn' | 'dismissed' | 'failed' | 'none';

/** the server-side logout (clears the site login, and the cms session for a nihul account) */
const LOGOFF_URL = '/users/LogOff/';
/** the xhr logout of a site (non-nihul) account: clears the login cookie, no redirect */
const LOGOUT_API = '/Users/UserLogout/';

export interface UserSeries {
    series: YeshivaModels.BE.ShiurSeries;
    viewed: number;
    lastUserViewed: number;
}

/** api/auth/passwordreset answers - the reset page switches on it */
export type PasswordResetStatus = 'ok' | 'invalid' | 'expired' | 'used' | 'badPassword' | 'badCaptcha';
export interface PasswordResetState {
    status: PasswordResetStatus;
}

/** api/subscription answers - the /users/unsubscribe page switches on the status */
export type UnsubscribeStatus = 'sent' | 'ok' | 'expired' | 'invalid' | 'badEmail' | 'badService' | 'badCaptcha';
export interface UnsubscribeState {
    status: UnsubscribeStatus;
    /** the normalized service (halacha | shiur | emuna | halachaeng | shiureng | all) */
    service?: string;
    removeAll?: boolean;
    /** confirmation only: the Reg_sub columns switched off */
    cleared?: string[];
}
export interface UnsubscribeRequest {
    email: string;
    serviceType: string;
    /** 'eng' for the English variant of the service (the English mails' links carry it) */
    serviceLanguage: string;
    removeAll: boolean;
    /** the invisible reCAPTCHA response */
    captcha: string;
}

/** api/subscription/subscribe answers - the /users/emuna page switches on the status */
export interface MailingJoinState {
    /** ok | alreadySubscribed | notLoggedIn | badService | failed */
    status: string;
}

/**
 * The logged-in user as /Users/UserName/ serializes it (reg_gen userYeshiva + its JsonProperty
 * names) - the json getUser() emits and applyUserData() takes. Anonymous = Id 0, or an empty object
 * on a logout emission. Only the fields the client reads are listed; the nihul flags stay behind
 * UserService's accessors.
 */
export interface SiteUser {
    Id: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    email?: string;
    isInternal?: boolean;
    PolicyAgree?: boolean;
    Permissions?: number;
    [key: string]: unknown;
}

@Injectable({
    providedIn: 'root',
})
export class UserService implements OnDestroy {
    protected userDataService = inject(UserDataService);
    private seriesService = inject(SeriesService);
    private _utils = inject(UtilService);
    private _http = inject(HttpClient);
    private _appRef = inject(ApplicationRef);
    private _i18n = inject(I18nService);
    private _platform = inject(BrowserPlatformService);
    private _injector = inject(Injector);
    //the Angular-owned user state (user-state.service.ts): the user json, the login-cookie rule,
    //the nihul flags and the server-revealed cms links. UserService is the facade the app reads it
    //through; the session bridge and the login UI live in lazy chunks, fetched on first use
    private readonly _state = inject(UserStateService);

    readonly REPLIES_API = '/Users/UserReplies/';
    readonly QUESTION_API = '/Users/UserQuestions/';

    //observable on user login and logout events.
    private _user_info_observable: Observable<any>;
    public lessonsLinkUrl: string = '';
    public updatedUserData: Subject<any>;
    private _subscriptions: Subscription[] = [];
    // The CMS base link exposed as a signal so links built from it stay reactive to login/logout.
    // Empty unless the user currently has internal access (the base link is server-only data), and
    // cleared on logout - consumers read this instead of the non-reactive cmsBaseLink() getter.
    private readonly _cmsManageBaseLink = signal('');
    readonly cmsManageBaseLink = this._cmsManageBaseLink.asReadonly();

    constructor() {
        //the emissions are the state's: the user json while logged in, {} once a resolve says
        //anonymous (the logout shape every subscriber tests for), nothing before the first resolve
        this._user_info_observable = this._platform.isBrowser
            ? this._state.changes$.pipe(
                  map((user) => (user.Id > 0 ? user : {})),
                  shareReplay({ bufferSize: 1, refCount: true }),
              )
            : EMPTY;

        this.updatedUserData = new BehaviorSubject<any>(null);
        this._subscriptions.push(
            this.userDataService.invokeUpdateUserData
                .pipe(
                    //update data only if 500 ms passed then last update invoke
                    // debounceTime(500),
                    throttleTime(500), // Not sure why this code used debounce rather than throttle.
                )
                .subscribe(() => {
                    if (this.userConnected()) {
                        //update user data
                        this.getUserDataSummary()
                            .then((data) => {
                                //notify user data for subscribers
                                this.updatedUserData.next(data);
                            })
                            .catch((error) => {
                                //return null value to the observer in order to keep it alive
                                this.updatedUserData.error(error);
                            });
                    } else {
                        //update subscribers that user is logoff
                        //notify null data to subscribers
                        // console.log('not connected');

                        this.updatedUserData.next(null);
                    }
                }),
        );

        //invoke update user data when user is logging in or logoff
        this._subscriptions.push(
            this.getUser().subscribe(() => {
                this._cmsManageBaseLink.set(this.hasInternalAccess() ? this.cmsBaseLink() : '');
                this.updateUserData();
                this.setLessonLink();
                //the emissions settle in http and cookie callbacks, and some subscribers still update
                //plain fields + markForCheck: one tick after ALL of them have written keeps every
                //consumer repainting in the production build. Cheap insurance (it was load-bearing
                //while the emissions came from the legacy UserBar.js callbacks)
                queueMicrotask(() => this._appRef.tick());
            }),
        );

        if (this._platform.isBrowser) {
            //resolve the user: the server check is forced on the English site, whose login cookie
            //may live on the Hebrew domain only
            void this._state.ready(!this._i18n.langIsHeb());
            //the cms transfer cookies, now and on every focus - a lazy chunk, off the bootstrap path
            void import('./user-session-bridge.service').then(({ UserSessionBridgeService }) =>
                this._injector.get(UserSessionBridgeService).start(),
            );
            //the nihul is the login MASTER on EVERY page, not just the nihul-type surfaces:
            //once per page load, a browser that ever held a nihul login (nihulLogonUrl stored)
            //re-checks and revives an expired site login as soon as possible - the reactive
            //consumers (content pencil, header icons, user summary) pick the change up from
            //the getUser() emission. Anonymous browsers have no stored logon url and skip
            //with ZERO requests. Deferred off the bootstrap path.
            AppGlobalsModule.setTimeoutIsNotServer(() => void this.ensureNihulUser(), 0);
        }
    }

    ngOnDestroy(): void {
        this._utils.unsubscribe(this._subscriptions);
        if (this.updatedUserData) {
            this.updatedUserData.unsubscribe();
        }
    }

    getUser(): Observable<any> {
        //if login - get user object with user info
        //if logout - get empty object
        return this._user_info_observable;
    }

    private _isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    /**
     *
     * @param forceCheck sometimes we don't have the cookie because the user is checked on the hebrew domain
     * @returns
     */
    userReady(forceCheck: boolean = false): Promise<boolean> {
        return this._state.ready(forceCheck);
    }

    //**
    // Indicate if user is connected or disconnected.
    // IMPORTANT: Internally it uses the cookie which cause DEBUGGING bug if login into mobile mode
    // and then desktop mode due to user agent change that cause the server to reject the connection.
    // */
    userConnected(): boolean {
        return this._state.isConnected();
    }

    /**
     * Indicate if user has internal access permissions.
     * @returns true if user is internal user
     */
    isInternal(): boolean {
        return this._state.isInternal();
    }

    /** Whether the user currently has internal (CMS edit) access: logged in, and internal. */
    hasInternalAccess(): boolean {
        return this._state.loggedIn() && this.isInternal();
    }

    /** The user is a cms user with any permission at all - may enter the /nihul shell. */
    nihulUser(): boolean {
        return this._nihulFlag('nihulUser');
    }

    /** The user has the new-nihul MIDRASH permission (the lessons area). */
    nihulMidrash(): boolean {
        return this._nihulFlag('nihulMidrash');
    }

    /** The user may access the new-nihul ASK area (the ASK permission or an answering-rabbi account). */
    nihulAsk(): boolean {
        return this._nihulFlag('nihulAsk');
    }

    /**
     * May create a שאלה יזומה: ask staff or an ask-identity rabbi. A midrash-only rabbi keeps
     * the questions area (their queue, via nihulAsk) but not question creation - the server
     * enforces the same (CreateQuestion 403s a rabbi with no ask id).
     */
    nihulCreateQuestion(): boolean {
        return this._nihulFlag('nihulCreateQuestion');
    }

    /** An answering rabbi limited to their own question queue (no midrash/ask/content permission). */
    nihulRabbiOnly(): boolean {
        return this._nihulFlag('nihulRabbiOnly');
    }

    /**
     * The account's rabbi identities (the user json's nihulRabbiIdentities: every central Rabbis
     * row on this login with its per-area ids). Normally one; a rabbi answering under their own
     * name AND an institutional one holds two, and the nihul offers them a switch over this list
     * (NihulRabbiIdentityService). An absent field reads as "no choice", which keeps the
     * server-side defaults in charge.
     */
    nihulRabbiIdentities(): { rabbiId: number; askHebId: number; askEngId: number; midrashId: number }[] {
        const fromData = this._state.user()?.['nihulRabbiIdentities'];
        if (!Array.isArray(fromData)) {
            return [];
        }
        return fromData
            .filter((row): row is Record<string, unknown> => this._isRecord(row))
            .map((row) => ({
                rabbiId: Number(row['rabbiId']) || 0,
                askHebId: Number(row['askHebId']) || 0,
                askEngId: Number(row['askEngId']) || 0,
                midrashId: Number(row['midrashId']) || 0,
            }));
    }

    /** The user has the ADMIN permission - may manage the cms users (/nihul/nihulusers). */
    nihulAdmin(): boolean {
        return this._nihulFlag('nihulAdmin');
    }

    /** The user has the PROMOTIONS permission - the email-lists (promotions) page. */
    nihulPromotions(): boolean {
        return this._nihulFlag('nihulPromotions');
    }

    /** The user has the STORE permission (store staff; STORE_ADMIN and ADMIN too, as supersets) - the Shabbat store area (/nihul/store). */
    nihulStore(): boolean {
        return this._nihulFlag('nihulStore');
    }

    /** The store's upper level (STORE_ADMIN, ADMIN as a superset): events/terms/settings editing, card reveal, price override, export. */
    nihulStoreAdmin(): boolean {
        return this._nihulFlag('nihulStoreAdmin');
    }

    /** An editing rabbi (RAV_MIDRASH + email matched to their rav account) - edits their OWN shiurim. */
    nihulRavMidrash(): boolean {
        return this._nihulFlag('nihulRavMidrash');
    }

    /**
     * The nihul address for THIS user's header entry link: a rabbi with only their question
     * queue goes straight to /nihul/ask, a rabbi editing only their own lessons to
     * /nihul/midrash, and any richer combination (both rabbi roles, or any staff permission)
     * to the nihul home. Cosmetic like all client gating - every target enforces its own
     * server authorization.
     */
    nihulEntryLink(): string {
        const rabbiOnly = this.nihulRabbiOnly();
        const ravMidrash = this.nihulRavMidrash();
        //nihulRabbiOnly already excludes MIDRASH/ASK/CanEdit but not the rest - a rabbi who is
        //also admin/promotions/store staff (or both rabbi roles) belongs on the nihul home
        const staff = this.isInternal() || this.nihulMidrash() || this.nihulAdmin() || this.nihulPromotions() || this.nihulStore();
        if (rabbiOnly && !ravMidrash && !staff) {
            return '/nihul/ask';
        }
        //nihulAsk is true for any answering-rabbi account, so a pure midrash rav has it false
        if (ravMidrash && !rabbiOnly && !staff && !this.nihulAsk()) {
            return '/nihul/midrash';
        }
        return '/nihul';
    }

    /** A nihul access flag of the user json (false while anonymous). */
    private _nihulFlag(field: string): boolean {
        return this._state.flag(field);
    }

    /**
     * The user's chosen main roles (/nihul/profile) as a UserPermissions bitmask - the nihul
     * menu narrows its TOP BAR to these areas. 0 = no choice = the full menu, which is also
     * what a missing field reads as (fail-open: a display preference must never hide anything
     * on its own).
     */
    nihulMainRoles(): number {
        return this._state.number('nihulMainRoles') ?? 0;
    }

    /**
     * The DEVELOPER permission - the maintenance tooling (/nihul/settings). ADMIN satisfies it
     * as a superset, so an admin keeps the page without being granted the flag explicitly.
     */
    nihulDeveloper(): boolean {
        return this._nihulFlag('nihulDeveloper');
    }

    /**
     * May manage the daily-halacha schedule (/nihul/halacha) - STRUCTURE or ASK, ADMIN as a
     * superset. Deliberately NOT nihulAsk, which is true for any answering-rabbi account:
     * rabbi-only accounts are excluded from the schedule.
     */
    nihulHalacha(): boolean {
        return this._nihulFlag('nihulHalacha');
    }

    //the rabbi ids and the server-revealed links of the user json (the json's own field names)
    askHebRavId(): number | undefined {
        return this._state.number('AskHebRavId');
    }

    askEngRavId(): number | undefined {
        return this._state.number('AskEngRavId');
    }

    midrashRavId(): number | undefined {
        return this._state.number('MidrashRavId');
    }

    setLessonLink(): void {
        this.lessonsLinkUrl = this.cmsApiLink() + this._state.link('cmsContentLink');
    }

    getLessonsLink(): string {
        return this.lessonsLinkUrl;
    }

    cmsBaseLink(): string {
        return this._state.link('cmsBaseLink');
    }

    cmsApiLink(): string {
        return this._state.link('cmsApiLink');
    }

    /**
     * Apply a fresh /Users/UserName-shaped json as the current user. Used by the nihul revive
     * (NihulAccessService) after the cms re-asserted an expired site login: the state's changes$
     * IS the getUser() stream, so the nihul flags and every subscriber update from it.
     */
    applyUserData(data: Record<string, unknown>): void {
        this._state.apply(data);
    }

    //one revive attempt per page load, shared by every nihul-type surface (the /nihul
    //app, the on-page edit controls, hofesh limud manage, reply moderation) so
    //concurrent checks all wait for the same answer instead of racing it
    private _nihulRevivePromise: Promise<void> | null = null;

    /**
     * The shared admission check for EVERY nihul-type surface: resolves the user's nihul
     * flags, reviving an expired site login from a live cms ticket when needed. A nihul
     * login's SHORT site cookie (AdminCookieMinutes) dies under the sliding cms ticket,
     * and the state's cookie short-circuit then answers "logged out" with no server call -
     * for YEARS that left the on-page nihuls (midrash/ask edit, hofesh limud, replies)
     * silently unavailable until a re-login.
     *
     * Anonymous traffic stays on the no-request fast path: without `force`, the server
     * is only asked when this browser held a nihul login at some point (the nihulLogonUrl
     * localStorage entry) - a browser that never did pays nothing. Pass force=true on
     * dedicated nihul pages (/nihul entry) where one POST is always worth it.
     */
    async ensureNihulUser(force = false): Promise<boolean> {
        if (!this._platform.isBrowser) {
            return false;
        }
        //any persisted nihul evidence (a stored cms link from a past real login) makes the
        //server check worth it; a browser with none pays nothing
        const worthAsking = force || !!(this.cmsApiLinkStored() || this.cmsLogonLinkStored());
        await this.userReady(worthAsking);
        if (!this.nihulUser() && worthAsking) {
            this._nihulRevivePromise ??= this._tryNihulRevive();
            await this._nihulRevivePromise;
        }
        return this.nihulUser();
    }

    /**
     * Is there a LIVE cms (nihul) forms session in this browser? The nihul flags above
     * come from db permissions and outlive the cms ticket, so a site-logged-in nihul
     * user can still have a dead cms session - callers probe this and send the user to
     * the cms logon instead of letting every management api call fail.
     */
    async cmsSessionAlive(): Promise<boolean> {
        if (!this._platform.isBrowser) {
            return false;
        }
        const apiLink = this.cmsApiLinkStored();
        if (!apiLink) {
            return false;
        }
        try {
            const response = await fetch(`${environment.nihulDomain}${apiLink}Session/Ping`, { credentials: 'include' });
            return response.ok && (await response.json()) === true;
        } catch {
            return false;
        }
    }

    /**
     * The cms api address. The nihul address is CLASSIFIED - no part of it may appear
     * as a literal in shipped code (not even a derivation token), so the ONLY sources
     * are the live server-revealed value (present while logged in) and the localStorage
     * copy the state persisted from that same revealed value at the last real login. No
     * stored address (this browser never held a nihul login) = empty = callers do nothing.
     */
    cmsApiLinkStored(): string {
        const live = this.cmsApiLink();
        if (live) {
            return live;
        }
        return this._storedLink('cmsApiLink');
    }

    /**
     * The cms logon address, same classified-source rules as cmsApiLinkStored: the live
     * server-revealed cmsLogonLink json field first, then the localStorage copy, then the
     * older nihulLogonUrl entry (browsers whose last login predates the cmsLogonUrl
     * persistence - it IS a logon address, possibly with a query to strip).
     */
    cmsLogonLinkStored(): string {
        return this._state.link('cmsLogonLink') || this._storedLink('cmsLogonUrl') || this._storedLink('nihulLogonUrl');
    }

    private _storedLink(key: string): string {
        try {
            return localStorage.getItem(key) ?? '';
        } catch {
            return '';
        }
    }

    /**
     * The site answered anonymous, but this browser may still hold a LIVE cms ticket
     * (the nihul is the login MASTER and its sliding ticket outlives the short site
     * cookie). Ask the cms app to re-assert the site login from its own ticket
     * (Session/Revive; works in the cms app's own process, so unlike UserName's decrypt
     * self-heal it needs no shared machineKey) and apply the returned user json -
     * every subscriber updates reactively.
     */
    private async _tryNihulRevive(): Promise<void> {
        if (!(await this.cmsSessionAlive())) {
            return;
        }
        try {
            //GET + no-store: per-user identity json must never come from the browser's http
            //cache, and varnish only caches allowlisted paths (this is not one). A bodyless
            //POST is not an option - the production chain 411s it
            const response = await fetch(`${environment.nihulDomain}${this.cmsApiLinkStored()}Session/Revive`, {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
            });
            if (!response.ok) {
                return;
            }
            const revivedUser = await response.json();
            if (revivedUser && revivedUser.Id) {
                this.applyUserData(revivedUser);
            }
        } catch {
            //a dead ticket 302s to the cms logon html (json() throws) - nothing to revive
        }
    }

    repliesLink(): string {
        return this.cmsApiLink() + this._state.link('repliesLink');
    }

    userLogin(): void {
        this.openLogin({ mode: 'login' });
    }

    userRegister(): void {
        this.openLogin({ mode: 'register' });
    }

    //--------------------------------------------------------------------------------------
    // the login UI (user/login): the panel lives inline on the /users page; everywhere else a
    // mid-task login opens it in a dialog. One dialog at a time.
    //--------------------------------------------------------------------------------------
    private _loginDialog: MatDialogRef<LoginDialogComponent, Record<string, unknown> | undefined> | null = null;

    /** Open the login dialog. All call sites go through here. */
    openLogin(data: LoginDialogData = {}): void {
        if (!this._platform.isBrowser) {
            return;
        }
        if (this._loginDialog) {
            //already open (a double click)
            return;
        }
        //the dialog, MatDialog and the panel live in a lazy chunk - fetched on the first login ask
        void import('./login/login-launcher').then(({ openLoginDialog }) => {
            if (this._loginDialog) {
                return;
            }
            this._loginDialog = openLoginDialog(this._injector, data);
            this._loginDialog.afterClosed().subscribe(() => {
                this._loginDialog = null;
            });
        });
    }

    /**
     * Log out through the server. A site account: /Users/UserLogout/ over xhr clears the login
     * cookie and the state emits anonymous, no reload. A nihul account navigates /users/LogOff/,
     * which also clears the cms session (a cross-origin hop on the English site).
     */
    userLogout(): void {
        const win = this._platform.nativeWindow;
        if (!win) {
            return;
        }
        if (this.isInternal()) {
            this._state.clear();
            win.location.href = LOGOFF_URL;
            return;
        }
        const logout = firstValueFrom(this._http.get(LOGOUT_API, { responseType: 'text', headers: { noCache: '1' } })).then(
            () => this._state.clear(),
            //the cookie may still be set - fall back to the navigating logout
            () => (win.location.href = LOGOFF_URL),
        );
        this._state.trackLogout(logout);
    }

    /** Close the login dialog if open (the user turned out to be connected already). */
    closeDialog(): void {
        this._loginDialog?.close(undefined);
    }

    /**
     * A nihul (cms) account logging in on the site: the cms logon page in a small overlay
     * (nihul-logon-dialog), completing through /users/login?nihulDone=1 and the session bridge.
     * The address is the one the server revealed - the login answer's location, or (header entry
     * icon) the copy a past login persisted; this code never knows it otherwise. Resolves once the
     * overlay closed: 'loggedIn' with the user applied, 'dismissed' when the visitor closed it
     * before the logon finished, 'failed' when the logon said done but the site stayed anonymous,
     * 'none' right away when no address is known.
     */
    nihulLogin(logonLocation = ''): Promise<NihulLoginOutcome> {
        const url = logonLocation || this.cmsLogonLinkStored();
        if (!url) {
            return Promise.resolve('none');
        }
        if (logonLocation) {
            try {
                this._platform.localStorage?.setItem('nihulLogonUrl', logonLocation);
            } catch {
                //storage blocked - the overlay still works for this page load
            }
        }
        this._loginDialog?.close(undefined);
        return Promise.all([import('./login/login-launcher'), this._state.logoutPending()]).then(
            ([{ openNihulLogonDialog }]) =>
                new Promise<NihulLoginOutcome>((resolve) => {
                    openNihulLogonDialog(this._injector, url)
                        .afterClosed()
                        .subscribe((outcome) => void this._afterNihulLogon(outcome === 'done').then(resolve));
                }),
        );
    }

    /**
     * The cms logon overlay closed. The logon left the site login in the transferLogin cookie:
     * apply it. Whatever the outcome - a visitor may close the overlay by hand after the logon
     * finished - and when no transfer cookie is readable (an http-only cookie, a proxy) the
     * server's answer decides: the site cookie IS set by then, so a plain refetch sees the user.
     */
    private async _afterNihulLogon(done: boolean): Promise<NihulLoginOutcome> {
        const { UserSessionBridgeService } = await import('./user-session-bridge.service');
        const transfer = this._injector.get(UserSessionBridgeService).sync();
        if (transfer !== 'login' && (done || this._state.hasLoginCookie())) {
            await this._state.refetch();
        }
        if (this._state.loggedIn()) {
            return 'loggedIn';
        }
        return done ? 'failed' : 'dismissed';
    }

    //invoke an update to user after data is change in backends
    public updateUserData(): void {
        this.userDataService.invokeUpdateUserData.next();
    }

    public getUserDataSummaryObservable(): Observable<any> {
        return this.updatedUserData.asObservable();
    }

    async getUserDataSummary(): Promise<any> {
        return this.userDataService.getDataSummary();
    }

    getUserContentHistory() {
        return this.userDataService.getDataField(UserActionType.History).toPromise();
    }

    getUserFavorits() {
        return this.userDataService.getDataField(UserActionType.Favorite).toPromise();
    }

    //UserSeriesService is only needed by the user pages (series summaries): root-provided and
    //dynamically imported here so it stays out of the initial bundle (the RavService pattern)
    private _userSeries(): Promise<UserSeriesService> {
        return import('./user-series.service').then(({ UserSeriesService }) => this._injector.get(UserSeriesService));
    }

    async getUserSeries() {
        return (await this._userSeries()).getAll();
    }

    /** the tracked series whose latest lesson the user has not seen yet */
    getUpdatedSerieses(): Promise<UserSeries[]> {
        return this._seriesSummary(true);
    }

    /** all the tracked series */
    getUserSeriesesSummary(): Promise<UserSeries[]> {
        return this._seriesSummary(false);
    }

    //todo: compare last time of user interaction with the series and time of the last lesson in the series
    private _seriesSummary(onlyUpdated: boolean): Promise<UserSeries[]> {
        return Promise.all([this.seriesService.getAllSeries(), this._userSeries().then((service) => service.getAll())]).then(([allSerieses, userSeries]) => {
            const summary: UserSeries[] = [];
            //foreach series in user data take the series (updated: those whose last content isn't in the viewed array)
            userSeries?.forEach((viewed: number[], id: number) => {
                const seriesInfo = allSerieses?.get(id);
                if (seriesInfo && (!onlyUpdated || !viewed.includes(seriesInfo.LastId))) {
                    summary.push({ series: seriesInfo, viewed: viewed.length, lastUserViewed: viewed[0] });
                }
            });
            //order by series update time desc
            return summary.sort((sA, sB) => (sA.series.DateUp > sB.series.DateUp ? 1 : -1)).reverse();
        });
    }

    getUserReplies(page = 0): Observable<YeshivaModels.BE.ReplyView[]> {
        return this._http.get<YeshivaModels.BE.ReplyView[]>(this.REPLIES_API + page, {
            params: {},
            headers: { noCache: 'true' },
        });
    }

    getUserQuestions(page = 0): Observable<YeshivaModels.BE.Question[]> {
        return this._http.get<YeshivaModels.BE.Question[]>(this.QUESTION_API + page, {
            headers: { noCache: 'true' },
        });
    }

    /**
     * Asks the server to mail a password-reset link. A POST with the fields in the body: the
     * reCAPTCHA token is ~4k characters and used to ride the query string of a GET, which
     * ASP.NET rejects above maxQueryStringLength (every recovery request failed once the
     * tokens grew, 2026-09-09) - and a secret has no business in a url anyway.
     * Register.aspx reads Request(), which covers form fields exactly like query parameters.
     */
    sendPasswordRecovery(email: string, captcha: string): Observable<boolean> {
        // If email is empty for some reason we don't want to send the request
        if (!email) {
            return new Observable<boolean>((observer) => {
                observer.error('Email is empty');
            });
        }
        const now = new Date();
        const fields = {
            what: 'sendPasswordRecovery',
            sendBy: 'email',
            email,
            'g-recaptcha-response': captcha,
            __date: now.getDate() + '/' + now.getMonth() + '/' + now.getFullYear(),
        };
        return this._http.post<boolean>('/reg/Register.aspx', AppGlobalsModule.jsonToForm(fields));
    }

    /** Is a password-reset link still redeemable (the /users/resetpassword page asks before showing its form). */
    getPasswordResetTokenState(token: string): Observable<PasswordResetState> {
        return this._http.get<PasswordResetState>('/api/auth/passwordresettoken', {
            params: { token },
            headers: { noCache: 'true' },
        });
    }

    /** Redeems a password-reset link for a new password (single use; the server also burns the user's other open links). */
    resetPassword(token: string, password: string, captcha: string): Observable<PasswordResetState> {
        return this._http.post<PasswordResetState>('/api/auth/passwordreset', { token, password, captcha });
    }

    /** Asks the server to mail the unsubscribe confirmation link (nothing changes until it is clicked). */
    requestUnsubscribe(request: UnsubscribeRequest): Observable<UnsubscribeState> {
        return this._http.post<UnsubscribeState>('/api/subscription/unsubscribe', request);
    }

    /** The optional "why are you leaving" note, mailed to the subscriptions inbox. */
    sendUnsubscribeReason(request: UnsubscribeRequest & { reason: string }): Observable<UnsubscribeState> {
        return this._http.post<UnsubscribeState>('/api/subscription/unsubscribereason', request);
    }

    /** The logged-in account joins a mailing list (emuna so far) - the mirror of the unsubscribe calls. */
    joinMailing(serviceType: string): Observable<MailingJoinState> {
        return this._http.post<MailingJoinState>('/api/subscription/subscribe', { serviceType });
    }

    /** Redeems the mailed unsubscribe link: clears the mailing flags it names. */
    confirmUnsubscribe(token: string): Observable<UnsubscribeState> {
        return this._http.post<UnsubscribeState>('/api/subscription/unsubscribeconfirm', { token });
    }

    updateRegistration(userData: any, captcha: string): Observable<string> {
        let jsonData = { act: 'update', 'g-recaptcha-response': captcha, ...userData };

        return this._http.post('/Users/Details/', AppGlobalsModule.jsonToForm(jsonData), {
            responseType: 'text',
        });
    }

    getUserData(): Observable<any> {
        return this._http.get('/Users/UserName?act=userDataJson', {
            responseType: 'json',
            headers: { noCache: 'true' },
        });
    }

    removeRegistration() {
        // http://he.local/Users/Details/
        // userID: 29703
        // act: remove
    }

    getRegistrationCountries(): Observable<Array<string>> {
        let lang = this._i18n.getLangshort();
        return this._http.get<Array<string>>(`/reg/Register.aspx?what=countriesJson&lan=${lang}`).pipe(take(1));
    }

    getRegistrationStates(): Observable<Array<string>> {
        let lang = this._i18n.getLangshort();
        return this._http.get<Array<string>>(`/reg/Register.aspx?what=statesJson&lan=${lang}`).pipe(take(1));
    }

    removeQuestion(id: number) {
        return this._http.post(`/Users/RemoveUserQuestion/${id}`, { _v: 1 }).pipe(tap(() => this.reduceQuestionsAmount()));
    }

    reduceQuestionsAmount() {
        this.getUserDataSummaryObservable()
            // Must happens once otherwise it will be infinie recursion
            .pipe(take(1))
            .subscribe({
                next: (data) => {
                    //notify user data for subscribers
                    data.server.all_answers -= 1;

                    this.updatedUserData.next(data);
                },
            });
        // An alternative we could have retake user data from server but it's redurant request
    }

    registerView(id: number): Observable<string> {
        return this._http.get(`/General/LogView/3/${id}`, { responseType: 'text' });
    }
}
